import {
  PHOTO_DRAG_THRESHOLD,
  QN_ANALYTICS_EVENT_BASE,
} from "@config/QuickNewsConstants";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { QNCardType } from "@typings/QuickNews";
import { useCallback, useMemo, useRef, useState } from "react";

export interface CarouselDragOptions {
  current: number;
  imagesLength: number;
  index: number;
  itemTitle: string;
  setActiveTab: (tab: QNCardType) => void;
  onPrev: () => void;
  onNext: () => void;
  analyticsPrefix: string;
  resetTranslateOnStart?: boolean;
  onSetCurrent: (newCurrent: number) => void;
}

export interface CarouselDragReturn {
  isDragging: boolean;
  currentTranslate: React.MutableRefObject<number>;
  handleTouchStart: (e: React.TouchEvent | React.MouseEvent) => void;
  handleTouchMove: (e: React.TouchEvent | React.MouseEvent) => void;
  handleTouchEnd: (e: React.TouchEvent | React.MouseEvent) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: (e: React.MouseEvent) => void;
  nextSlide: () => void;
  prevSlide: () => void;
}

type Direction = "horizontal" | "vertical" | "undetermined";

const WRONG_TOUCH_ZONE = 10; // Threshold to distinguish between horizontal and vertical gestures

export const useCarouselDrag = ({
  current,
  imagesLength,
  itemTitle,
  setActiveTab,
  onPrev,
  onNext,
  analyticsPrefix,
  resetTranslateOnStart = false,
  onSetCurrent,
}: CarouselDragOptions): CarouselDragReturn => {
  const sendAnalyticsEvent = useTMCustomEventAnalyticsCall();

  const [isDragging, setIsDragging] = useState(false);

  const [gestureDirection, setGestureDirection] =
    useState<Direction>("undetermined");
  const startX = useRef(0);
  const startY = useRef(0);
  const currentTranslateY = useRef(0);
  const currentTranslate = useRef(0);

  /** Extract coordinate helpers */
  const getClientXY = (e: React.TouchEvent | React.MouseEvent) => {
    if ("touches" in e) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    const mouseEvent = e;
    return { x: mouseEvent.clientX, y: mouseEvent.clientY };
  };

  const analyticsKeys = useMemo(
    () => ({
      swipeLeft: `${analyticsPrefix}::swipe left`,
      swipeRight: `${analyticsPrefix}::swipe right`,
      clickLeft: `${analyticsPrefix}::click left`,
      clickRight: `${analyticsPrefix}::click right`,
    }),
    [analyticsPrefix],
  );

  const fireAnalytics = useCallback(
    (action: string, newIndex: number) => {
      sendAnalyticsEvent({
        ...QN_ANALYTICS_EVENT_BASE,
        eventAction: `${action}::${newIndex}`,
        eventLabel: itemTitle,
      });
    },
    [sendAnalyticsEvent, itemTitle],
  );

  /** Touch Start */
  const handleTouchStart = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);
      setGestureDirection("undetermined");

      const { x, y } = getClientXY(e);
      startX.current = x;
      startY.current = y;

      if (resetTranslateOnStart) {
        currentTranslate.current = 0;
        currentTranslateY.current = 0;
      }
    },
    [resetTranslateOnStart],
  );

  /** Touch Move */
  const handleTouchMove = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!isDragging) return;

      const { x, y } = getClientXY(e);
      const translateX = x - startX.current;
      const translateY = y - startY.current;
      if (gestureDirection === "undetermined") {
        if (
          Math.abs(translateX) > WRONG_TOUCH_ZONE ||
          Math.abs(translateY) > WRONG_TOUCH_ZONE
        ) {
          if (Math.abs(translateX) > Math.abs(translateY)) {
            setGestureDirection("horizontal");
          } else {
            setGestureDirection("vertical");
          }
        }
      }

      if (gestureDirection === "horizontal") {
        e.preventDefault();
        currentTranslate.current = translateX;
      } else if (gestureDirection === "vertical") {
        currentTranslateY.current = translateY;
      }
    },
    [isDragging, gestureDirection],
  );

  /** Touch End */
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      e.stopPropagation();
      setIsDragging(false);

      const movedX = currentTranslate.current;
      const movedY = currentTranslateY.current;

      /** HORIZONTAL SWIPES */
      if (gestureDirection === "horizontal") {
        if (movedX > PHOTO_DRAG_THRESHOLD && current > 0) {
          const newIndex = current - 1;
          fireAnalytics(analyticsKeys.swipeLeft, newIndex);
          onSetCurrent(newIndex);
        } else if (movedX < -PHOTO_DRAG_THRESHOLD) {
          if (current < imagesLength - 1) {
            const newIndex = current + 1;
            fireAnalytics(analyticsKeys.swipeRight, newIndex);
            onSetCurrent(newIndex);
          } else {
            setActiveTab("Videos");
          }
        }
      }

      /** VERTICAL SWIPES */
      if (gestureDirection === "vertical") {
        if (movedY > PHOTO_DRAG_THRESHOLD) {
          onPrev();
        } else if (movedY < -PHOTO_DRAG_THRESHOLD) {
          onNext();
        }
      }

      currentTranslate.current = 0;
      currentTranslateY.current = 0;
      setGestureDirection("undetermined");
    },
    [
      current,
      gestureDirection,
      fireAnalytics,
      analyticsKeys.swipeLeft,
      analyticsKeys.swipeRight,
      onSetCurrent,
      imagesLength,
      setActiveTab,
      onPrev,
      onNext,
    ],
  );

  /** Mouse wrappers */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleTouchStart(e);
    },
    [handleTouchStart],
  );
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleTouchMove(e);
    },
    [handleTouchMove],
  );
  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      handleTouchEnd(e);
    },
    [handleTouchEnd],
  );

  /** Button Controls */
  const nextSlide = useCallback(() => {
    const newIndex = current === imagesLength - 1 ? 0 : current + 1;
    fireAnalytics(analyticsKeys.clickRight, newIndex);
    onSetCurrent(newIndex);
  }, [current, imagesLength, analyticsKeys, fireAnalytics, onSetCurrent]);

  const prevSlide = useCallback(() => {
    const newIndex = current === 0 ? imagesLength - 1 : current - 1;
    fireAnalytics(analyticsKeys.clickLeft, newIndex);
    onSetCurrent(newIndex);
  }, [current, imagesLength, analyticsKeys, fireAnalytics, onSetCurrent]);

  return {
    isDragging,
    currentTranslate,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    nextSlide,
    prevSlide,
  };
};
